// Function index:
//   HexTile             — Honeycomb pointy-top hex factory.
//   buildGrid(states)   — Honeycomb Grid<Hex> from the local hex state map.
//   drawHex(g, corners, color) — fill+stroke a hex outline.
//   installPanZoom(canvas, camera) — wire pan+zoom on the camera Container.
//   drawSelectionRing(ring, corners) — yellow outline for the selected hex.

import type { Container, Graphics } from 'pixi.js';
import { defineHex, Grid, Orientation } from 'honeycomb-grid';
import type { HexState } from './types.js';

const HEX_SIZE = 36;
export const HexTile = defineHex({ dimensions: HEX_SIZE, orientation: Orientation.POINTY });

export function buildGrid(states: Iterable<HexState>): Grid<InstanceType<typeof HexTile>> {
  return new Grid(HexTile, [...states].map(({ q, r }) => ({ q, r })));
}

export function drawHex(g: Graphics, corners: Array<{ x: number; y: number }>, color: number): void {
  const c0 = corners[0]!;
  g.moveTo(c0.x, c0.y);
  for (let i = 1; i < corners.length; i++) g.lineTo(corners[i]!.x, corners[i]!.y);
  g.closePath().fill({ color }).stroke({ color: 0x000, width: 1, alpha: 0.4 });
}

export function installPanZoom(canvas: HTMLCanvasElement, camera: Container): void {
  let panning = false; let lastX = 0; let lastY = 0;
  canvas.addEventListener('pointerdown', (e) => {
    if (e.button === 2 || e.button === 1) { panning = true; lastX = e.clientX; lastY = e.clientY; }
  });
  window.addEventListener('pointerup', () => { panning = false; });
  window.addEventListener('pointermove', (e) => {
    if (!panning) return;
    camera.x += e.clientX - lastX;
    camera.y += e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
  });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    camera.scale.x = Math.min(Math.max(camera.scale.x * factor, 0.3), 3);
    camera.scale.y = camera.scale.x;
  }, { passive: false });
}

export function drawSelectionRing(
  ring: Graphics,
  corners: Array<{ x: number; y: number }>,
): void {
  ring.clear();
  const c0 = corners[0]!;
  ring.moveTo(c0.x, c0.y);
  for (let i = 1; i < corners.length; i++) ring.lineTo(corners[i]!.x, corners[i]!.y);
  ring.closePath().stroke({ color: 0xffeb88, width: 3 });
}
